import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { AlertTriangle, Check, Copy, Server, Sparkles, UploadCloud, X } from 'lucide-react';
import kgDatasetService from '../../../services/kgDatasetService';
import config, { getApiKey } from '../../../config/environment';
import { copyTextToClipboard } from '../../../utils/clipboard';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import KgDryRunReport from '../KgDryRunReport';
import styles from './LoadDataTab.module.scss';
import {
  DRY_RUN_SAMPLE_SIZE,
  PUSH_MAX_BATCH,
  SERVER_TO_SERVER_THRESHOLD,
  addPushCounts,
  analyzeRecordKeys,
  chunkRecords,
  parseRecordKey,
  readDuplicateReport,
  readRecordsFromFiles,
  recordKeyIsDerived,
  describeError,
  emptyPushTotals,
  formatNumber,
  mappingGaps,
  readEmbedRatio,
  recordCount,
  recordNoun,
} from '../kgUtils';

/** A facet below this share of records is worth a second look before loading. */
const LOW_COVERAGE_RATIO = 0.1;
/** Below this share of text embedded, semantic search will disappoint. */
const LOW_EMBED_RATIO = 0.25;

/**
 * Load data (screen 4).
 *
 * There is no file-upload endpoint — the API takes JSON arrays capped at 500
 * records per request, so THE CLIENT OWNS THE CHUNKING. That makes this real UI
 * work rather than a wrapper around one call:
 *
 *   1. read + parse, confirm it is an array of objects
 *   2. dry-run a sample FIRST — free, writes nothing, and where a wrong mapping
 *      gets caught before 100k records go in under it
 *   3. push batches SEQUENTIALLY; concurrent pushes to one dataset just contend
 *   4. accumulate counts and show running progress, with a working cancel
 *   5. on failure, STOP and name the batch — the upsert is idempotent, so the
 *      recovery is to fix and re-push from there, which is only possible if the
 *      user is told where it stopped
 *
 * What gets pushed is the file's records EXACTLY as they were read. No
 * flattening, no unwrapping of JSON-string columns, no reshaping of any kind —
 * the server does all of that on the way in, through `mapping_config.preprocessing`,
 * and a record altered here would no longer match the paths the mapping was
 * written against.
 */
const LoadDataTab = ({ dataset, sampleHandover, onSamples, onMutated, notify, goToTab }) => {
  const fileInputRef = useRef(null);
  const cancelRef = useRef(false);

  const [dragOver, setDragOver] = useState(false);
  const [reading, setReading] = useState(false);
  const [records, setRecords] = useState(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState(null);

  const [dryRun, setDryRun] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunError, setDryRunError] = useState(null);

  const [pushing, setPushing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [totals, setTotals] = useState(null);
  const [pushError, setPushError] = useState(null);
  const [done, setDone] = useState(false);
  // Duplicate row identities the SERVER saw, summed over the batches actually
  // pushed. `unchanged` is a normal outcome; this is not — a duplicate means one
  // record overwrote another on the way in.
  const [pushDuplicates, setPushDuplicates] = useState(0);
  // The mapping_version the records were loaded under. If the mapping is saved
  // again afterwards the button reopens as "Re-load", because a new mapping only
  // applies to records pushed after it.
  const [loadedVersion, setLoadedVersion] = useState(null);
  const [copied, setCopied] = useState(false);

  const batches = useMemo(
    () => (records ? chunkRecords(records, PUSH_MAX_BATCH) : []),
    [records]
  );
  const tooBig = records && records.length > SERVER_TO_SERVER_THRESHOLD;

  const pushUrl = `${config.API_BASE_URL}/kg-datasets/${dataset.key}/records`;
  const curlSnippet = [
    `curl -X POST '${pushUrl}' \\`,
    `  -H 'x-api-key: ${getApiKey() || 'YOUR_API_KEY'}' \\`,
    "  -H 'Content-Type: application/json' \\",
    `  -d '{"records": [ ... up to ${PUSH_MAX_BATCH} records ... ]}'`,
  ].join('\n');

  const resetRun = () => {
    setLoadedVersion(null);
    setDryRun(null);
    setDryRunError(null);
    setTotals(null);
    setProgress(null);
    setPushError(null);
    setDone(false);
    setPushDuplicates(0);
  };

  const readFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setParseError(null);
    setReading(true);
    resetRun();
    try {
      const { records: parsed, errors, fileCount } = await readRecordsFromFiles(files);
      if (parsed.length === 0) {
        setRecords(null);
        setParseError(
          errors[0] ||
            'No records found. Each file should hold a record object, or an array of them.'
        );
        return;
      }
      setRecords(parsed);
      setFileName(fileCount === 1 ? files[0].name : `${formatNumber(fileCount)} files`);
      // Skipping a few bad files out of hundreds is normal; say so, but do not
      // discard everything that did parse.
      setParseError(
        errors.length > 0
          ? `${errors.length} file${errors.length === 1 ? '' : 's'} couldn't be read and ${errors.length === 1 ? 'was' : 'were'} skipped. First: ${errors[0]}`
          : null
      );
      // Share the sample with the mapping editor so its column pickers and live
      // check start working too.
      onSamples(parsed.slice(0, 200));
      await runDryRun(parsed);
    } finally {
      setReading(false);
    }
  };

  const runDryRun = async (array) => {
    setDryRunning(true);
    setDryRunError(null);
    try {
      const response = await kgDatasetService.dryRun(
        dataset.mapping_config,
        array.slice(0, DRY_RUN_SAMPLE_SIZE)
      );
      setDryRun(response);
    } catch (e) {
      setDryRunError(describeError(e, "Couldn't check this file against the mapping."));
    } finally {
      setDryRunning(false);
    }
  };

  /** Adopt the records carried over from the create wizard. */
  const adoptHandoverRecords = async () => {
    const parsed = sampleHandover?.records || [];
    if (parsed.length === 0) return;
    setParseError(null);
    resetRun();
    setRecords(parsed);
    setFileName(`${formatNumber(parsed.length)} records from setup`);
    onSamples(parsed.slice(0, 200));
    await runDryRun(parsed);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    readFiles(event.dataTransfer?.files);
  };

  // ── The push loop ──────────────────────────────────────────────────────

  const handlePush = async () => {
    cancelRef.current = false;
    setPushing(true);
    setPushError(null);
    setDone(false);
    setPushDuplicates(0);
    let running = emptyPushTotals();
    setTotals(running);
    // Summed the same way the counts are: each batch reports only its own.
    let duplicates = 0;
    // Tracked locally, not via state: the loop needs to know whether it stopped
    // early on the very next line, and a setState hasn't landed by then.
    let stopped = false;

    for (let i = 0; i < batches.length; i += 1) {
      if (cancelRef.current) {
        stopped = true;
        setPushError(
          `Cancelled after batch ${i} of ${batches.length}. Everything up to record ${formatNumber(
            i * PUSH_MAX_BATCH
          )} is loaded — re-push from there when you're ready.`
        );
        break;
      }
      setProgress({ batch: i + 1, of: batches.length });
      try {
        // Sequential on purpose: each push commits and enqueues embedding work,
        // and parallel pushes to one dataset only contend with each other.
        // eslint-disable-next-line no-await-in-loop
        const response = await kgDatasetService.pushRecords(dataset.key, batches[i]);
        running = addPushCounts(running, response?.counts);
        setTotals(running);
        duplicates += readDuplicateReport(response?.report)?.duplicateCount || 0;
        setPushDuplicates(duplicates);
      } catch (e) {
        stopped = true;
        const from = i * PUSH_MAX_BATCH + 1;
        const to = Math.min((i + 1) * PUSH_MAX_BATCH, records.length);
        // Naming the batch is the whole point: the upsert is idempotent, so the
        // fix is to correct the problem and re-push from exactly here.
        setPushError(
          `Stopped on batch ${i + 1} of ${batches.length} (records ${formatNumber(
            from
          )}–${formatNumber(to)}): ${describeError(e)}`
        );
        break;
      }
    }

    setPushing(false);
    setProgress(null);
    if (!stopped) {
      setDone(true);
      setLoadedVersion(dataset.mapping_version ?? null);
    }
    await onMutated({ reloadFacets: true });
  };

  // A saved mapping change reopens the load — the new config only applies to
  // records pushed under it, so re-pushing is the only way to apply it to these.
  const mappingChangedSinceLoad =
    done && loadedVersion !== null && dataset.mapping_version !== loadedVersion;
  const alreadyLoaded = done && !mappingChangedSinceLoad;

  /**
   * Would this load silently discard records?
   *
   * Two records sharing a record_key are ONE record — the second overwrites the
   * first. The server reports this too, in the dry-run report below and in every
   * push report, but only over the records in that request: a key repeated
   * across batch 3 and batch 40 shows up in neither. The whole file is in memory
   * here, so this is the only check that sees all of it.
   *
   * It cannot run on a DERIVED key — the value doesn't exist until the server
   * builds it — so in that case the server's report is the only source, and this
   * stays quiet rather than guessing.
   */
  const keyIsDerived = recordKeyIsDerived(dataset.mapping_config);
  const keyAnalysis = useMemo(
    () =>
      keyIsDerived ? null : analyzeRecordKeys(records, dataset.mapping_config?.record_key),
    [records, dataset.mapping_config, keyIsDerived]
  );
  const recordKeyLabel = parseRecordKey(dataset.mapping_config?.record_key).join(' + ');


  /**
   * The last cheap moment to change your mind.
   *
   * Loading records locks the text config permanently — the original text is not
   * retained, so a wrong `embed` choice can only be undone by deleting the
   * dataset. Anything the dry-run already knows that would be expensive to
   * discover later belongs in front of the user HERE, not in a report they have
   * to interpret.
   */
  const preLoadWarnings = useMemo(() => {
    if (!dryRun?.report || done) return [];
    const out = [];
    const total = Number(dryRun.report.counts?.total || 0);
    const coverage = dryRun.report.facet_coverage || {};

    const weak = Object.entries(coverage)
      .filter(([, n]) => total > 0 && Number(n) / total < LOW_COVERAGE_RATIO)
      .map(([name, n]) => `${name} (${Math.round((Number(n) / total) * 100)}%)`);
    if (weak.length) {
      out.push(
        `${weak.length} facet${weak.length === 1 ? '' : 's'} barely populated: ${weak.join(', ')}. Counts over ${weak.length === 1 ? 'it' : 'them'} will report a floor, not a total. Worth checking the column is right before this becomes your data.`
      );
    }

    // No title / nothing embedded / no facets. The server returns these as
    // dry-run warnings too, and the report above shows them — repeated here in
    // the load screen's own words because THIS is the point of no return for the
    // text settings, and "records show as bare ids everywhere" is easier to act
    // on before the data is in than after.
    out.push(...mappingGaps(dataset.mapping_config));

    const preview = Array.isArray(dryRun.preview) ? dryRun.preview : [];
    // Every preview record with a null date means date filters and "how many
    // last month" are dead on arrival.
    if (preview.length > 0 && preview.every((p) => !p?.event_at)) {
      out.push(
        'No dates resolved on any sampled record — date filters and “last month” style questions will return nothing. Check the date column and its format.'
      );
    }
    // Denominator is `full_text_chars` — the document's real length.
    // `total_text_chars` is a read-tier planner input, not a length, and using it
    // made a fully-embedded record read as "108% embedded".
    const ratios = preview.map(readEmbedRatio);
    const embedded = ratios.reduce((sum, r) => sum + r.embedded, 0);
    const totalChars = ratios.reduce((sum, r) => sum + r.full, 0);
    if (totalChars > 0 && embedded < totalChars && embedded / totalChars < LOW_EMBED_RATIO) {
      out.push(
        `Only ${Math.round((embedded / totalChars) * 100)}% of your text is being embedded. The agent will filter and count these records fine, but "what did this say about X?" will match very little. Turn on more text fields now if that matters — this is the last chance.`
      );
    }
    return out;
  }, [dryRun, done, dataset.mapping_config]);

  const pushedSoFar = totals ? totals.received : 0;
  const pct = records ? Math.min(100, (pushedSoFar / records.length) * 100) : 0;

  return (
    <div className={styles.wrap}>
      {/* ── 1. The file ── */}
      {/* The sample from the create wizard is right here in memory. Making
          someone re-select the same files they chose ninety seconds ago — and
          leaving them wondering why "0 news" after uploading 26 of them — is the
          gap this closes. Only offered when we still hold the complete set. */}
      {!records && sampleHandover?.complete && recordCount(dataset) === 0 && (
        <section className={`${styles.section} ${styles.handoff}`}>
          <h3 className={styles.sectionTitle}>
            <Sparkles size={15} />
            Load the records you already chose
          </h3>
          <p className={styles.sectionHelp}>
            The {formatNumber(sampleHandover.total)}{' '}
            {recordNoun(dataset, sampleHandover.total)} you uploaded while setting this up were
            used to draft the mapping — nothing was written. Load them now, or choose different
            files below.
          </p>
          <div className={styles.actions}>
            <Button
              variant="contained"
              className={sharedStyles.primaryBtn}
              onClick={adoptHandoverRecords}
            >
              Use those {formatNumber(sampleHandover.total)} records
            </Button>
          </div>
        </section>
      )}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Load a JSON export</h3>
        <p className={styles.sectionHelp}>
          The file is read here in your browser and pushed in batches of{' '}
          {PUSH_MAX_BATCH}. Re-pushing a record you already loaded overwrites it rather than
          duplicating it, so running the same file twice is safe — and mostly free.
        </p>

        <div
          className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ''}`}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);
          }}
          onClick={() => !pushing && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
        >
          <UploadCloud size={32} color="var(--text-secondary)" />
          <span className={styles.dropTitle}>
            {reading ? 'Reading…' : fileName || 'Drop your export here, or click to choose'}
          </span>
          <span className={styles.dropHint}>
            {records
              ? `${formatNumber(records.length)} ${recordNoun(dataset, records.length)} · ${
                  batches.length
                } batch${batches.length === 1 ? '' : 'es'}`
              : 'One file with an array, or many files with one record each'}
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            readFiles(e.target.files);
            e.target.value = '';
          }}
        />

        {parseError && <Alert severity="error">{parseError}</Alert>}

        {tooBig && (
          <Alert severity="warning" icon={<Server size={18} />}>
            <strong>
              {formatNumber(records.length)} records is too many to move through a browser.
            </strong>{' '}
            That is {formatNumber(batches.length)} sequential requests. Push it server-to-server
            instead — the endpoint and your key are below.
          </Alert>
        )}
      </section>

      {/* ── 2. Check before pushing ── */}
      {records && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Check the mapping first
            {dryRunning && <CircularProgress size={14} />}
          </h3>
          <p className={styles.sectionHelp}>
            This runs your current mapping over the first{' '}
            {formatNumber(Math.min(records.length, DRY_RUN_SAMPLE_SIZE))} records without writing
            anything. A facet reading 0% here will read 0% across the whole file.
          </p>
          {dryRunError ? (
            <Alert severity="warning">{dryRunError}</Alert>
          ) : (
            <KgDryRunReport
              result={dryRun}
              totalFacets={dataset.mapping_config?.facets?.length || 0}
              recordKeyLabel={recordKeyLabel}
              onFixRecordKey={() => goToTab('mapping')}
            />
          )}
          {keyAnalysis?.collapsed && (
            <Alert severity="error" icon={<AlertTriangle size={18} />}>
              <strong>
                This would keep {formatNumber(keyAnalysis.distinct)} of{' '}
                {formatNumber(keyAnalysis.keyed)} records.
              </strong>
              <p className={styles.warnBody}>
                The row identity is{' '}
                <code>{[].concat(dataset.mapping_config?.record_key || []).join(' + ')}</code>,
                and {formatNumber(keyAnalysis.keyed)} records share only{' '}
                {formatNumber(keyAnalysis.distinct)} distinct values of it. Records with the same
                identity are the same record — each one overwrites the last, so{' '}
                {formatNumber(keyAnalysis.lost)} would be discarded on the way in.
                {keyAnalysis.worst.length > 0 && (
                  <>
                    {' '}Most repeated:{' '}
                    {keyAnalysis.worst.map((w) => `${w.key} (×${w.count})`).join(', ')}.
                  </>
                )}
              </p>
              <Button size="small" variant="text" onClick={() => goToTab('mapping')}>
                Fix the row identity
              </Button>
            </Alert>
          )}

          {preLoadWarnings.length > 0 && (
            <Alert severity="warning" icon={<AlertTriangle size={18} />}>
              <strong>Worth fixing before you load.</strong>
              <ul className={styles.warnList}>
                {preLoadWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
              <span className={styles.warnFoot}>
                Loading locks the text settings permanently — the original text isn’t kept, so
                changing them afterwards means deleting the dataset and starting again. Facets
                and dates stay editable.
              </span>
            </Alert>
          )}

          <div className={styles.actions}>
            <Button
              variant="text"
              size="small"
              onClick={() => goToTab('mapping')}
              disabled={pushing}
            >
              Adjust the mapping
            </Button>
            {/* Four states, because leaving it live after a finished load reads
                as "did that work?" — even though a re-push is idempotent. */}
            {alreadyLoaded ? (
              <span className={styles.loadedTag}>
                <Check size={14} /> Loaded
              </span>
            ) : (
              <Tooltip title={tooBig ? 'Use the API for a file this size.' : ''}>
                <span>
                  <Button
                    variant={keyAnalysis?.collapsed ? 'outlined' : 'contained'}
                    color={keyAnalysis?.collapsed ? 'error' : undefined}
                    className={keyAnalysis?.collapsed ? undefined : sharedStyles.primaryBtn}
                    onClick={handlePush}
                    disabled={pushing || dryRunning || tooBig}
                    startIcon={pushing ? <CircularProgress size={14} /> : null}
                  >
                    {pushing
                      ? `Loading… ${formatNumber(pushedSoFar)} of ${formatNumber(records.length)}`
                      : keyAnalysis?.collapsed
                        ? `Load anyway — only ${formatNumber(keyAnalysis.distinct)} of ${formatNumber(records.length)} will be kept`
                        : mappingChangedSinceLoad
                          ? `Re-load ${formatNumber(records.length)} ${recordNoun(dataset, records.length)}`
                          : `Looks right — load ${formatNumber(records.length)} ${recordNoun(dataset, records.length)}`}
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        </section>
      )}

      {/* ── 3. Progress ── */}
      {(pushing || totals) && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {pushing ? 'Loading' : done ? 'Loaded' : 'Stopped'}
          </h3>

          {pushing && (
            <>
              <LinearProgress
                variant="determinate"
                value={pct}
                className={styles.progressBar}
              />
              <div className={styles.progressRow}>
                <span>
                  Batch {progress?.batch ?? 0} of {progress?.of ?? batches.length} ·{' '}
                  {formatNumber(pushedSoFar)} of {formatNumber(records.length)} sent
                </span>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<X size={13} />}
                  onClick={() => {
                    cancelRef.current = true;
                  }}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}

          {totals && (
            <div className={styles.totalsRow}>
              <span className={`${styles.totalChip} ${styles.ok}`}>
                <strong>{formatNumber(totals.ingested)}</strong> new or changed
              </span>
              {/* `unchanged` is a deliberate no-op, not a failure. A nightly full
                  re-push is mostly this, and that is what makes it affordable. */}
              <Tooltip title="Byte-identical to what is already stored, so nothing was re-embedded. This is what makes a repeat sync cheap.">
                <span className={styles.totalChip}>
                  <strong>{formatNumber(totals.unchanged)}</strong> already up to date
                </span>
              </Tooltip>
              {totals.failed > 0 && (
                <button
                  type="button"
                  className={`${styles.totalChip} ${styles.bad}`}
                  onClick={() => goToTab('history')}
                >
                  <strong>{formatNumber(totals.failed)}</strong> failed — see why
                </button>
              )}
              {/* Not a failure count and not an "unchanged" count — a third
                  thing, and the only one that means records went missing. */}
              {pushDuplicates > 0 && (
                <Tooltip title="These records repeated a row identity already in this load, so each one overwrote the last. Only the final version of each survived.">
                  <button
                    type="button"
                    className={`${styles.totalChip} ${styles.bad}`}
                    onClick={() => goToTab('mapping')}
                  >
                    <strong>{formatNumber(pushDuplicates)}</strong> overwritten by a duplicate
                    identity
                  </button>
                </Tooltip>
              )}
            </div>
          )}

          {pushDuplicates > 0 && (
            <Alert severity="warning" icon={<AlertTriangle size={18} />}>
              <strong>
                {formatNumber(pushDuplicates)} record
                {pushDuplicates === 1 ? '' : 's'} did not survive the load.
              </strong>
              <p className={styles.warnBody}>
                They shared a row identity (<code>{recordKeyLabel || 'record key'}</code>) with
                another record in the same push, and a push keeps the last one for each identity.
                If these were meant to be separate records, add another column to the identity in
                the mapping and re-push — the upsert is idempotent, so running the same file
                again is safe.
              </p>
              <Button size="small" variant="text" onClick={() => goToTab('mapping')}>
                Fix the row identity
              </Button>
            </Alert>
          )}

          {pushError && <Alert severity="error">{pushError}</Alert>}

          {mappingChangedSinceLoad && (
            <Alert severity="info">
              The mapping changed after these records were loaded. Existing records keep the
              facets they came in with — re-load to apply the new mapping to them.
            </Alert>
          )}

          {done && (
            <Alert severity="success" icon={<Check size={18} />}>
              <strong>Records and counts are live now.</strong> “How many” and “which ones”
              already answer correctly. The text is still being embedded in the background, so
              questions about what a record <em>says</em> will start working as that finishes —
              watch the progress bar at the top of this page.
              {dataset.status === 'draft' && (
                <>
                  {' '}
                  This dataset is still a draft, so no agent can see it yet.
                </>
              )}
            </Alert>
          )}

          {done && (
            <div className={styles.actions}>
              <Button variant="text" size="small" onClick={() => goToTab('records')}>
                Browse what was loaded
              </Button>
            </div>
          )}
        </section>
      )}

      {/* ── 4. The API, for anything large or scheduled ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Or push from your own system</h3>
        <p className={styles.sectionHelp}>
          For a scheduled sync, or anything above roughly a million records, push straight to the
          API. Batches of {PUSH_MAX_BATCH}, one request at a time. Records already loaded are
          skipped automatically.
        </p>
        <div className={styles.snippetHead}>
          <code className={styles.endpoint}>{pushUrl}</code>
          <Button
            size="small"
            variant="text"
            startIcon={copied ? <Check size={13} color="#10b981" /> : <Copy size={13} />}
            onClick={async () => {
              try {
                await copyTextToClipboard(curlSnippet);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              } catch (e) {
                notify('Copy failed — select the snippet and copy it manually.', 'warning');
              }
            }}
          >
            {copied ? 'Copied' : 'Copy example'}
          </Button>
        </div>
        <pre className={styles.snippet}>{curlSnippet}</pre>
        <Typography variant="caption" className={styles.keyWarning}>
          That snippet includes your API key — treat it like a password.
        </Typography>
      </section>
    </div>
  );
};

export default LoadDataTab;
